import React, { FC, memo, ReactNode } from 'react';
import { useMutation } from 'react-query';
import { BusinessPartnersAPI } from 'api/BusinessPartnersAPI';
import { ExclamationCircleFilled } from '@ant-design/icons/lib';
import { Button, message, Modal, Tooltip } from 'antd';
import { ButtonProps } from 'antd/lib/button';
import { DeleteOutlined } from '@ant-design/icons';
import { BusinessPartner } from 'models/businessPartner';
import { useFormatMessage } from 'common/hooks/useFormatMessage';

export interface BusinessPartnerDeleteButtonProps
  extends Omit<ButtonProps, 'onClick' | 'loading' | 'href'> {
  businessPartner: BusinessPartner;
  tooltip?: ReactNode;
  onDeleted?: () => void;
}

export const BusinessPartnerDeleteButton: FC<BusinessPartnerDeleteButtonProps> = memo(
  ({ businessPartner, onDeleted, tooltip, ...buttonProps }) => {
    const [mutate, { isLoading }] = useMutation(
      BusinessPartnersAPI.deleteEntity
    );
    const intlMessage = useFormatMessage();

    const confirm = () =>
      Modal.confirm({
        title: intlMessage(
          { id: 'businessPartners.delete.confirmation' },
          {
            name: businessPartner.name,
          }
        ),
        content: intlMessage({ id: 'users.willBeDeleted' }),
        icon: (
          <ExclamationCircleFilled
            style={{
              color: 'red',
              fontSize: '35px',
              marginTop: '-5px',
            }}
          />
        ),
        okText: intlMessage({ id: 'yes' }),
        cancelText: intlMessage({ id: 'cancel' }),
        okType: 'danger',
        onOk: () =>
          mutate(businessPartner, {
            onSuccess: () => {
              message.success(
                intlMessage(
                  { id: 'businessPartners.delete.success' },
                  {
                    name: businessPartner.name,
                  }
                )
              );
              onDeleted && onDeleted();
            },
          }),
      });

    const button = (
      <Button
        id="button-delete-business-partner"
        loading={isLoading}
        onClick={() => confirm()}
        icon={<DeleteOutlined />}
        {...buttonProps}
      />
    );

    return tooltip ? <Tooltip title={tooltip}>{button}</Tooltip> : button;
  }
);
